/**
 * The PILOT and MECH rail pages' bodies (`decisions/0206`): the unit sheet in its BUILD posture.
 * EDIT mode turns the same sheet editable in place (`MechSheetEdit.tsx`, `PilotSheetEdit.tsx`). `RailPanel` resolves which pilot a page shows and binds the
 * navigations; these decide what the page renders for it, and are rendered directly by their tests.
 *
 * Who may do what is decided here, once: the owner's own page gets the NOTES writer and the roll
 * buttons; the GM's (opened from ROSTER) gets neither, but both may switch to EDIT and both see the
 * carried wear. Another player never reaches these pages: SQUAD renders the sheets read-only.
 */
import type { ReactNode } from "react";
import { mechScope, pilotScope, resolveActiveMechId, type PilotData } from "@lancer-vtt/domain";
import { useCompendiumContext } from "../../lib/CompendiumProvider";
import { useGameSessionContext } from "../../lib/game/GameSessionProvider";
import type { UseRoster } from "../../lib/roster/useRemoteRoster";
import type { Ref } from "../compendium/compendiumData";
import { usePostedRoll, type RollEntry } from "./shell/railDice";
import { MechBuildSheet } from "./MechBuildSheet";
import { MechHangar } from "./MechHangar";
import { MechWearBlock } from "./MechWearBlock";
import { PilotBuildSheet } from "./PilotBuildSheet";
import type { MechMode, PilotMode } from "./railState";

/** What a sheet page needs from its host: the roster, the viewer's name lookup and the openers. */
export type SheetPageHost = {
  roster: UseRoster;
  slotName: (slotId: string | null) => string;
  onOpenInCompendium: (ref: Ref) => void;
  /** The viewer's dice verbs; the owner's sheets roll flat in place through `rolls.roll`. */
  rolls: RollEntry;
};

/** The mech a MECH page shows: its own id, else the pilot's active mech (players fly one mech nearly every session). */
export function mechPageMechId(pilot: PilotData, mechId: string | undefined): string | undefined {
  return mechId ?? resolveActiveMechId(pilot);
}

export function PilotPage({
  host,
  pilot,
  mode,
  onMode,
  onOpenMech,
}: {
  host: SheetPageHost;
  pilot: PilotData;
  mode: PilotMode;
  onMode: (mode: PilotMode) => void;
  onOpenMech: (mechId: string) => void;
}) {
  const session = useGameSessionContext();
  const { roster } = host;
  const computed = roster.computed[pilot.id];
  const actorId = session.actorId!;
  // A roll on a GM's view would post a player's stat as the GM's roll.
  const own = session.role !== "gm" && pilot.owner === actorId;
  const scope = pilotScope(pilot.id);
  const otherPilots = Object.values(roster.state?.pilots ?? {})
    .filter((p) => p.id !== pilot.id)
    .map((p) => ({ id: p.id, callsign: p.callsign }));
  return (
    <div className="gms-content unit-sheet-host shell-sheet-page">
      <PilotBuildSheet
        pilot={pilot}
        computed={computed}
        playerName={pilot.owner === null ? undefined : host.slotName(pilot.owner)}
        onOpenInCompendium={host.onOpenInCompendium}
        onOpenMech={onOpenMech}
        onCommitNotes={own ? (value) => roster.dispatch({ type: "set_pilot_text", actorId, pilotId: pilot.id, field: "notes", value }) : null}
        onRoll={own ? host.rolls.roll : null}
        showCarried
        // The owner and the GM edit here; the roster room's ownership gate is the wall behind it.
        edit={{
          dispatch: roster.dispatch,
          actorId,
          editing: mode === "edit",
          onEditing: (on) => onMode(on ? "edit" : "view"),
          undo: () => roster.undo(scope),
          redo: () => roster.redo(scope),
          undoState: roster.undoState(scope),
          otherPilots,
        }}
      />
    </div>
  );
}

export function MechPage({
  host,
  pilot,
  mechId,
  mode,
  onOpenMech,
  onCreateMech,
}: {
  host: SheetPageHost;
  pilot: PilotData;
  /** The page's mech id, if the path names one; without it the page opens the active mech. */
  mechId: string | undefined;
  /** `edit` is the sheet with EDIT on (`/mech/<id>/edit`). */
  mode: MechMode;
  /** `mechId` undefined opens the pilot's hangar without one (the player's `/mech/hangar`). */
  onOpenMech: (mechId: string | undefined, mode?: MechMode) => void;
  onCreateMech: () => void;
}) {
  const session = useGameSessionContext();
  const { compendium } = useCompendiumContext();
  const { roster } = host;
  const computed = roster.computed[pilot.id];
  const actorId = session.actorId!;
  const isGm = session.role === "gm";
  const rollDice = usePostedRoll();
  const id = mechPageMechId(pilot, mechId);
  const mech = id !== undefined ? pilot.mechs.find((m) => m.id === id) : undefined;
  // The hangar: asked for, or the landing page of a pilot with no mech (`decisions/0206` point 2).
  if (mode === "hangar" || id === undefined) {
    return (
      <div className="gms-content unit-sheet-host shell-sheet-page">
        <MechHangar
          pilot={pilot}
          computed={computed}
          actorId={actorId}
          dispatch={roster.dispatch}
          backTo={mech && mechId !== undefined ? { id: mech.id, name: mech.name } : undefined}
          onOpen={(m) => onOpenMech(m)}
          onCreate={onCreateMech}
        />
      </div>
    );
  }
  if (!mech) return <PageNote text="This mech is no longer on file." action={<button type="button" className="gms-tab" onClick={() => onOpenMech(pilot.mechs[0]?.id, "hangar")}>OPEN HANGAR</button>} />;
  const count = pilot.mechs.length;
  const isActive = resolveActiveMechId(pilot) === mech.id;
  const scope = mechScope(mech.id);
  return (
    <div className="gms-content unit-sheet-host shell-sheet-page">
      <MechBuildSheet
        pilot={pilot}
        mech={mech}
        computed={computed}
        playerName={pilot.owner === null ? undefined : host.slotName(pilot.owner)}
        onOpenInCompendium={host.onOpenInCompendium}
        onCommitNotes={(value) => roster.dispatch({ type: "set_mech_notes", actorId, pilotId: pilot.id, mechId: mech.id, notes: value })}
        onRoll={!isGm && pilot.owner === actorId ? host.rolls.roll : null}
        // The owner's (or the GM's) wear and Rest controls; the block renders nothing for anyone else.
        wear={
          <MechWearBlock
            pilot={pilot}
            mechId={mech.id}
            compendium={compendium}
            roster={roster}
            actorId={session.actorId}
            isGm={isGm}
            activeEncounterId={session.activeEncounterId}
            pilotMaxHp={computed?.combat.hp}
            // The banner plays for the GM and the mech's owner.
            onFullRepair={() =>
              session.sendFx(
                { type: "full-repair", payload: { sub: `${(pilot.callsign || pilot.name).toUpperCase()} · ${(mech.name || "MECH").toUpperCase()}` } },
                { slotIds: [actorId, ...(pilot.owner !== null ? [pilot.owner] : [])] },
              )
            }
          />
        }
        band={
          isActive ? undefined : (
            <div className="unit-sheet-notactive" role="status">
              <span>{isGm ? `NOT ${(pilot.callsign || pilot.name).toUpperCase()}'S ACTIVE MECH` : "NOT YOUR ACTIVE MECH"}</span>
              <button type="button" className="unit-sheet-action" onClick={() => roster.dispatch({ type: "set_active_mech", actorId, pilotId: pilot.id, mechId: mech.id })}>
                MAKE ACTIVE
              </button>
            </div>
          )
        }
        actions={
          <button type="button" className="unit-sheet-action" onClick={() => onOpenMech(mech.id, "hangar")}>
            HANGAR · {count} MECH{count === 1 ? "" : "S"}
          </button>
        }
        // The owner and the GM edit here; the roster room's ownership gate is the wall behind it.
        edit={{
          dispatch: roster.dispatch,
          actorId,
          editing: mode === "edit",
          onEditing: (on) => onOpenMech(mech.id, on ? "edit" : undefined),
          undo: () => roster.undo(scope),
          redo: () => roster.redo(scope),
          undoState: roster.undoState(scope),
          rollDice,
        }}
      />
    </div>
  );
}

function PageNote({ text, action }: { text: string; action?: ReactNode }) {
  return (
    <div className="gms-content">
      <div className="shell-note">
        {text}
        {action}
      </div>
    </div>
  );
}
